import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { BaseField } from "./BaseField";
import { FieldProps } from "modules/record/record-producer/types/record-producer-types";
import { useReferenceSearch } from "modules/record/record-producer/hooks/useReferenceFieldSearch";
import { useDebounce } from "shared/hooks/useDebounce";
import { ReferenceAutocompleteInput } from "modules/record/shared/components/ReferenceAutocompleteInput";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { useReferenceDisplayColumn } from "modules/record/shared/hooks/useReferenceOptions";
import { resolveDynamicReferenceTableName } from "modules/record/shared/services/dynamicReference";
import { useTables } from "app/hooks/useTables";
import { buildRequiredRule, getRequiredFieldMessage } from "../../services/recordProducerValidation";
import { getFieldHelperText } from "../../services/fieldHelperText";
import { RecordProducerContext } from "../RecordProducerContext";
import { groupInheritedColumnOptions } from "modules/record/shared/services/inheritedColumnOptions";

export const ReferenceFieldComponent = ({ column, control, baseFieldProps, runtimeProps }: FieldProps) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");
    const [loadOptions, setLoadOptions] = useState(false);
    const { setValue } = useFormContext();
    const { data: tables = [] } = useTables();
    const recordProducer = useContext(RecordProducerContext);
    const recordProducerColumns = recordProducer?.columns ?? [];
    const previousDependencyValueRef = useRef<unknown>(undefined);
    const selectedId = useWatch({
        control,
        name: column.name,
    });
    const dependencyColumn = useMemo(
        () => recordProducerColumns.find((candidate) => candidate.id === column.depends_on_column_id),
        [column.depends_on_column_id, recordProducerColumns]
    );
    const dependencyValue = useWatch({
        control,
        name: dependencyColumn?.name || "__dynamic_reference_dependency__",
    });
    const referenceTableName = useMemo(
        () => resolveDynamicReferenceTableName({
            column,
            columns: recordProducerColumns,
            tables,
            dependencyValue,
        }) || column.referenced_table_name,
        [column, dependencyValue, recordProducerColumns, tables]
    );
    const debouncedSearch = useDebounce(search, 400);
    const { displayColumnName } = useReferenceDisplayColumn(referenceTableName);
    const { data = [], isLoading, tableChainTargetId: selectedTableId } = useReferenceSearch(
        referenceTableName,
        column.reference_filter,
        selectedId,
        debouncedSearch,
        displayColumnName,
        loadOptions
    );
    const isDynamicReferenceWithoutTable = column.column_type === "DYNAMIC_REFERENCE" && !referenceTableName;
    const groupedOptions = useMemo(() => referenceTableName === "sys_columns"
        ? groupInheritedColumnOptions(data, tables.find((table) => table.id === selectedTableId) || null, tables,
            (origin) => String(t("inheritance.column.inherited_from", { table: origin })),
            (table) => translateMetadataLabel(t, table))
        : data, [data, referenceTableName, selectedTableId, t, tables]);

    useEffect(() => {
        if (column.column_type !== "DYNAMIC_REFERENCE") {
            return;
        }

        const previousDependencyValue = previousDependencyValueRef.current;
        previousDependencyValueRef.current = dependencyValue;

        if (previousDependencyValue === undefined || previousDependencyValue === dependencyValue || !selectedId) {
            return;
        }

        setValue(column.name, null, { shouldDirty: true, shouldValidate: true });
    }, [column.column_type, column.name, dependencyValue, selectedId, setValue]);

    return (
        <BaseField column={column} required={runtimeProps?.required} hidden={runtimeProps?.hidden} {...baseFieldProps} as="group">
            <Controller
                name={column.name}
                control={control}
                rules={{ required: buildRequiredRule(runtimeProps?.required ?? column.is_mandatory, getRequiredFieldMessage(column)) }}
                render={({ field, fieldState }) => (
                    <ReferenceAutocompleteInput
                        options={groupedOptions}
                        groupBy={referenceTableName === "sys_columns" ? (option) => option.originGroup : undefined}
                        showConcreteClass={tables.some(table => table.parent_table_name === referenceTableName)}
                        loading={isLoading}
                        selectedId={field.value}
                        displayField={displayColumnName}
                        referenceTable={referenceTableName}
                        onChange={field.onChange}
                        onSearchChange={setSearch}
                        onOpenOptions={() => setLoadOptions(true)}
                        disabled={runtimeProps?.readonly || isDynamicReferenceWithoutTable}
                        error={!!fieldState.error}
                        helperText={isDynamicReferenceWithoutTable
                            ? t("Select the reference table first")
                            : getFieldHelperText(fieldState, runtimeProps)}
                        showOpenButton
                        fieldLabel={translateMetadataLabel(t, column)}
                        openButtonTitle={String(t("Open selected record"))}
                    />
                )}
            />
        </BaseField>
    );
};
